import React, { useEffect } from "react";
import { GridLoader } from "react-spinners";

/**
 * Full-screen "we're charging your card" overlay.
 *
 * Covers the gap between the customer clicking Pay in the Accept UI modal and
 * the confirmation page rendering. That gap is not short: the browser posts a
 * nonce, the server charges the gateway, creates the order, sends mail and
 * fires analytics. With nothing on screen the Accept UI modal simply vanishes
 * and the customer is returned to a checkout page that looks untouched -- which
 * reads as "it didn't work" and invites a second attempt.
 *
 * Deliberately NOT dismissible. There is no cancel: the charge is already in
 * flight and there is nothing safe to cancel it with. Blocking the page is the
 * point -- it is what stops a second submit.
 */
const PaymentProcessingOverlay = ({
  visible,
  message = "Processing your payment…",
}) => {
  // Lock body scroll while shown. Without this the page scrolls behind the
  // overlay on mobile, which makes a blocking state feel broken.
  useEffect(() => {
    if (!visible) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      // role=status + aria-live announces this to screen readers, which
      // otherwise get no signal that anything is happening.
      role="status"
      aria-live="polite"
      aria-busy="true"
      style={{
        position: "fixed",
        inset: 0,
        // Must beat AcceptUI's own backdrop, which its injected stylesheet
        // gives `z-index: 999998` on #AcceptUIBackground. At a lower value the
        // modal's dimmer would render OVER this overlay while it tears down.
        zIndex: 1000000,
        backgroundColor: "rgba(255, 255, 255, 0.94)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 24,
        padding: 24,
        textAlign: "center",
      }}
    >
      <GridLoader color="#3b72fe" loading size={14} />

      <div>
        <div style={{ fontSize: 18, fontWeight: 600, color: "#111" }}>
          {message}
        </div>
        <div
          style={{
            marginTop: 8,
            fontSize: 14,
            color: "#666",
            lineHeight: 1.5,
            maxWidth: 320,
          }}
        >
          Please don’t close or refresh this page.
        </div>
      </div>
    </div>
  );
};

export default PaymentProcessingOverlay;
